import { cva } from 'class-variance-authority'

export const teaserCardItemSliderVariants = cva(
  ['relative w-full flex flex-nowrap justify-start overflow-hidden last:mr-0'],
  {
    variants: {
      width: {
        single: '',
        double: '',
      },
      size: {
        mini: 'mr-2.5 md:mr-3.5',
        small:
          '!w-[calc(36%+theme(spacing.5))] sm:!w-[calc((100%-theme(spacing[6.25])*3)/4)] lg:!w-[calc((100%-theme(spacing.10)*3)/4)] mr-10 sm:mr-6.25 lg:mr-13.75',
        medium: '!w-[calc(70%+theme(spacing.5))] mr-10 sm:mr-6.25 lg:mr-13.75',
        large: [
          'inline-block mb-7.5 !w-[calc(80%-theme(spacing.10))] sm:mb-0 mr-10 sm:mr-7.5 lg:mr-17.5',
        ],
      },
      isSelected: {
        true: '',
        false: '',
      },
      showActive: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        /** calculation for width explained:
         * single: !w-[calc((100%-(14px*7))/8)] ->
         * 100% sliderWrapper width
         * - 14px * 7 (gapWidth * possible gaps when only the chosen widthType(in this case single) is selected)
         * divided by 8 (amount of slides)
         *
         * double: !w-[calc((100%-(14px*3))/8*2)] ->
         * 100% sliderWrapper width
         * - 14px * 3 (gapWidth * possible gaps when only the chosen widthType(in this case double) is selected) + mobile only: sliderOffsetBefore and After (20px + 20px)
         * divided by 8 (amount of slides)
         * 2 (because double width)
         */
        width: 'single',
        size: 'mini',
        class:
          '!w-[calc((100%-(10px*3))/4)] sm:!w-[calc((100%-(10px*3))/4)] md:!w-[calc((100%-(14px*5))/6)] lg:!w-[calc((100%-(14px*7))/8)]',
      },
      {
        width: 'double',
        size: 'mini',
        class:
          '!w-[calc((100%-(10px))/4*2)] sm:!w-[calc((100%-10px)/4*2)] md:!w-[calc((100%-(14px*2))/6*2)] lg:!w-[calc((100%-(14px*3))/8*2)]',
      },
      {
        size: 'mini',
        showActive: true,
        class: 'first:ml-2 max-md:!mr-1 max-sm:!mr-2.5 cursor-pointer',
      },
      {
        size: 'mini',
        isSelected: true,
        showActive: true,
        class: '!scale-[1.05] origin-bottom pb-[5%]',
      },
      {
        width: 'single',
        size: 'medium',
        class:
          'sm:!w-[calc((100%-theme(spacing[12.5]))/3)] lg:!w-[calc((100%-theme(spacing[27.5]))/3)]',
      },
      {
        width: 'double',
        size: 'medium',
        class:
          'sm:!w-[calc((100%-theme(spacing[6.25]))/3*2)] lg:!w-[calc((100%-theme(spacing[27.5])))/3*2)]',
      },
      {
        width: 'double',
        size: 'large',
        class: 'sm:m-0 sm:!w-full',
      },
      {
        width: 'single',
        size: 'large',
        class:
          'sm:!w-[calc((100%-theme(spacing[7.5]))/2)] lg:!w-[calc((100%-theme(spacing[17.5]))/2)]',
      },
    ],
  }
)

export const teaserCardItemVariants = cva('w-full relative', {
  variants: {
    groupType: {
      slider: 'inline-block py-0 sm:block sm:p-0',
      grid: 'w-[80%]',
      'mobile-slider': '',
      'small-images': '',
    },
    type: {
      full: '',
      half: '',
    },
    size: {
      mini: 'w-full',
      small: '',
      medium: 'w-full',
      large: 'sm:w-full',
    },
    width: {
      single: '',
      double: '',
    },
  },
  compoundVariants: [
    {
      groupType: 'grid',
      type: 'half',
      size: 'mini',
      class: 'mb-2.5',
    },
    {
      groupType: 'grid',
      width: 'double',
      class: 'col-end-[span_2] w-[93%] sm:w-full',
    },
    {
      groupType: 'grid',
      size: 'large',
      class: 'mb-12.5 w-full shrink-0 sm:mb-0',
    },
    {
      groupType: 'grid',
      width: 'double',
      size: 'large',
      class: 'col-end-[span_2]',
    },
    {
      groupType: 'slider',
      size: 'large',
      class: 'inline-block w-full',
    },
  ],
})

export const teaserCardItemMediaVariants = cva(
  'w-full h-full overflow-hidden',
  {
    variants: {
      size: {
        mini: '',
        small: 'mb-[26px] md:mb-8',
        medium: '',
        large: '',
      },
      type: {
        full: '',
        half: 'mb-5',
      },
      width: {
        single: '',
        double: '',
      },
      groupType: {
        grid: '',
        slider: '',
        'mobile-slider': '',
        'small-images': '',
      },
      showActive: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      /** calculation for double media height explained:
       * !h-[calc((1400px-14px*7-200px)/8*4/3)] ->
       * 1400px (or 100vw) is the width of the viewport/container,
       * 14px is the gap between the slides,
       * 7 is the max. amount of gaps when single width is selected,
       * 200px is the padding of the container,
       * divided by 8 (amount of slides if single would be selected)
       * * 4/3 (aspect ratio)
       */
      // mini
      {
        size: 'mini',
        type: 'half',
        width: 'double',
        class:
          'aspect-auto !h-[calc((100vw-10px*3-40px)/4*1/1)] sm:!h-[calc((100vw-10px*3-80px)/4*1/1)] md:!h-[calc((100vw-14px*5-120px)/6*1/1)] lg:!h-[calc((100vw-14px*7-200px)/8*1/1)] xl:!h-[calc((100vw-14px*7-240px)/8*1/1)] 2xl:!h-[calc((1920px-14px*7-320px)/8*1/1)]',
      },
      {
        size: 'mini',
        type: 'full',
        width: 'double',
        class:
          'aspect-auto !h-[calc((100vw-10px*3-40px)/4*4/3)] sm:!h-[calc((100vw-10px*3-80px)/4*4/3)] md:!h-[calc((100vw-14px*5-120px)/6*4/3)] lg:!h-[calc((100vw-14px*7-200px)/8*4/3)] xl:!h-[calc((100vw-14px*7-240px)/8*4/3)] 2xl:!h-[calc((1920px-14px*7-320px)/8*4/3)]',
      },
      {
        size: 'mini',
        type: 'half',
        width: 'single',
        class: 'aspect-square',
      },
      {
        size: 'mini',
        type: 'full',
        width: 'single',
        class: 'aspect-[3/4]',
      },
      {
        size: 'mini',
        type: 'half',
        class: 'mb-2.5',
      },
      {
        size: 'mini',
        showActive: true,
        class: 'rounded-full m-1.5 mt-3.5 md:m-4 sm:mt-8',
      },
      {
        size: 'medium',
        type: 'half',
        width: 'double',
        class:
          'aspect-square sm:aspect-auto sm:!h-[calc((100vw-theme(spacing.35))/3)] md:!h-[calc((100vw-theme(spacing.45))/3)] lg:!h-[calc((100vw-theme(spacing.80))/3)] 2xl:!h-[calc(theme(screens[xl])/3)]',
      },
      {
        size: 'medium',
        type: 'full',
        width: 'double',
        class:
          'aspect-[4/5] sm:aspect-auto sm:!h-[calc((100vw-theme(spacing.35))/3*(3/2))] md:!h-[calc((100vw-theme(spacing.45))/3*3/2)] lg:!h-[calc((100vw-theme(spacing.80))/3*3/2)] 2xl:!h-[calc(theme(screens[xl])/3*3/2)]',
      },
      {
        size: 'medium',
        type: 'full',
        width: 'single',
        class: 'aspect-[2/3]',
      },
      {
        size: 'medium',
        type: 'half',
        width: 'single',
        class: 'aspect-square',
      },
      {
        size: 'medium',
        type: 'half',
        class: 'mb-6.25 md:mb-7.5',
      },
      {
        groupType: 'slider',
        size: 'large',
        type: 'half',
        width: 'single',
        class: 'aspect-[4/5] sm:aspect-[4/3] sm:mb-7.5',
      },
      {
        groupType: 'slider',
        size: 'large',
        type: 'full',
        width: 'single',
        class: 'aspect-[4/7] sm:aspect-[5/7]',
      },
      {
        groupType: 'slider',
        size: 'large',
        type: 'full',
        width: 'double',
        class: 'aspect-[4/7] sm:aspect-[3/2]',
      },
      {
        groupType: 'slider',
        size: 'large',
        type: 'half',
        width: 'double',
        class:
          'aspect-[5/7] sm:aspect-auto sm:h-[calc((100vw-30px-60px-80px)/2*(3/4))] sm:mb-7.5',
      },
      {
        groupType: 'grid',
        size: 'large',
        type: 'half',
        width: 'single',
        class: 'aspect-[1/1] sm:aspect-[4/3]',
      },
      {
        groupType: 'grid',
        size: 'large',
        type: 'half',
        width: 'double',
        class: 'aspect-[4/5] sm:aspect-auto',
      },
      {
        groupType: 'grid',
        size: 'large',
        type: 'full',
        width: 'single',
        class: 'aspect-[4/5] sm:aspect-[5/7]',
      },
      {
        groupType: 'grid',
        size: 'large',
        type: 'full',
        width: 'double',
        class:
          'aspect-[2/3] sm:aspect-auto sm:h-[calc((100vw-30px-60px-80px)/2*(7/5))] md:h-[calc((100vw-30px-220px-120px)/2*(7/5))] lg:h-[calc((100vw-60px-220px-200px)/2*(7/5))]',
      },
    ],
  }
)
